// Fatia S1: cadastro com teste grátis (negócio ou agência).
import { useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Briefcase, Store } from 'lucide-react'
import { TRIAL_DAYS, type AccountKind } from '@impulso/shared'
import type { SignupResponse } from '@impulso/shared/api/identity'
import { api, errorMessage } from '../../lib/api'
import { useSession } from '../../lib/session'
import AuthShell, { FormError } from '../../features/auth/AuthShell'
import PasswordInput from '../../features/auth/PasswordInput'
import { safeNext } from '../../features/auth/session-helpers'

export default function Signup() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { refresh } = useSession()
  const [kind, setKind] = useState<AccountKind>(params.get('tipo') === 'agencia' ? 'agency' : 'business')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [orgName, setOrgName] = useState('')
  const [terms, setTerms] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (password.length < 8) return setError('A senha precisa ter pelo menos 8 caracteres.')
    if (!terms) return setError('Para continuar, aceite os termos de uso e a política de privacidade.')
    setBusy(true)
    try {
      const r = await api.post<SignupResponse>('/auth/signup', {
        name, email, password, accountKind: kind, acceptTerms: true,
        ...(kind === 'business' ? { companyName: orgName } : { agencyName: orgName }),
      })
      await refresh()
      navigate(safeNext(r.next), { replace: true })
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell wide title="Crie sua conta" sub={`${TRIAL_DAYS} dias grátis, sem cartão. Você só paga se gostar.`} footer={<>Já tem conta? <Link className="strong auth-hit" style={{ color: 'var(--primary)' }} to="/login">Entrar</Link></>}>
      <form className="stack" style={{ gap: 14 }} onSubmit={submit}>
        <FormError error={error} />
        <div className="grid g-2" style={{ gap: 10 }} role="radiogroup" aria-label="Tipo de conta">
          <button type="button" role="radio" aria-label="Sou um negócio" aria-checked={kind === 'business'} className={`option-card ${kind === 'business' ? 'on' : ''}`} onClick={() => setKind('business')} style={{ padding: 14 }}>
            <span className="ic purple"><Store size={18} /></span>
            <span><span className="strong">Sou um negócio</span><br /><span className="small muted">Quero anunciar o meu</span></span>
            <span className="radio" />
          </button>
          <button type="button" role="radio" aria-label="Sou agência" aria-checked={kind === 'agency'} className={`option-card ${kind === 'agency' ? 'on' : ''}`} onClick={() => setKind('agency')} style={{ padding: 14 }}>
            <span className="ic blue"><Briefcase size={18} /></span>
            <span><span className="strong">Sou agência</span><br /><span className="small muted">Cuido de vários clientes</span></span>
            <span className="radio" />
          </button>
        </div>
        <div className="field">
          <label htmlFor="org">{kind === 'business' ? 'Nome do seu negócio' : 'Nome da agência'}</label>
          <input id="org" className="input" value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder={kind === 'business' ? 'Ex.: Doce Ateliê' : 'Ex.: Júlia Marketing'} required minLength={2} />
        </div>
        <div className="field">
          <label htmlFor="name">Seu nome</label>
          <input id="name" className="input" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
        </div>
        <div className="field">
          <label htmlFor="email">E-mail</label>
          <input id="email" className="input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="field">
          <label htmlFor="password">Crie uma senha</label>
          <PasswordInput id="password" value={password} onChange={setPassword} autoComplete="new-password" describedBy="password-hint" minLength={8} required />
          <p id="password-hint" className="small muted auth-hint">Pelo menos 8 caracteres.</p>
        </div>
        <label className="row small" style={{ alignItems: 'flex-start', gap: 8 }}>
          <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} style={{ marginTop: 3 }} />
          <span>Li e aceito os <Link to="/termos" target="_blank" className="strong" style={{ color: 'var(--primary)' }}>termos de uso</Link> e a <Link to="/privacidade" target="_blank" className="strong" style={{ color: 'var(--primary)' }}>política de privacidade</Link>.</span>
        </label>
        <button type="submit" className="btn primary lg block" disabled={busy}>{busy ? 'Criando sua conta…' : 'Começar o teste grátis'}</button>
        <p className="small muted" style={{ textAlign: 'center' }}>O dinheiro dos anúncios é pago direto às plataformas de anúncio. Nós cobramos só a assinatura.</p>
      </form>
    </AuthShell>
  )
}
